import { __assign, __read, __rest, __spread } from "tslib";
import React, { Fragment, useState, useContext } from "react";
import classNames from "classnames";
import { TableContext } from "../../TableContext";
import { TableDraggableContext } from "../../TableDraggableContext";
import { Icon } from "../../../icon";
import { noop } from "../../../_util/noop";
import { mergeRefs } from "../../../_util/merge-refs";
import { rowsDraggable } from "./rowsDraggable";
import { injectPropsIfTargetNotExisted } from "../../util/inject-props-if-target-not-existed";
var fallbackColumnKey = "__draggable_addon__";
var cellPaddingWidth = 20;
var reorder = function (list, startIndex, endIndex) {
    var result = Array.from(list);
    if (typeof endIndex === "undefined") {
        return result;
    }
    var _a = __read(result.splice(startIndex, 1), 1), removed = _a[0];
    result.splice(endIndex, 0, removed);
    return result;
};
export function draggable(_a) {
    var module = _a.module, _b = _a.width, width = _b === void 0 ? 26 : _b, full = _a.full, _c = _a.icon, icon = _c === void 0 ? "drop" : _c, hideHandler = _a.hideHandler, targetColumnKey = _a.targetColumnKey, _d = _a.onDragEnd, onDragEnd = _d === void 0 ? noop : _d, _e = _a.onDragStart, onDragStart = _e === void 0 ? noop : _e, _f = _a.draggableRowKeys, draggableRowKeys = _f === void 0 ? "all" : _f, _g = _a.childrenColName, childrenColName = _g === void 0 ? "childrenList" : _g, _h = _a.dragType, dragType = _h === void 0 ? "row" : _h, relations = _a.relations, onExpandedKeysChange = _a.onExpandedKeysChange, expandedKeys = _a.expandedKeys, _j = _a.indent, indent = _j === void 0 ? 20 : _j;
    // 支持新的行拖拽，轻量版本
    if (!module) {
        return rowsDraggable({
            onDragEnd: onDragEnd,
            onDragStart: onDragStart,
            draggableRowKeys: draggableRowKeys,
            childrenColName: childrenColName,
            dragType: dragType,
            relations: relations,
            onExpandedKeysChange: onExpandedKeysChange,
            expandedKeys: expandedKeys,
            indent: indent,
            icon: icon,
            hideHandler: hideHandler,
            width: width,
            targetColumnKey: targetColumnKey,
        });
    }
    var Draggable = module.Draggable;
    var total = null;
    return {
        getInfo: function () { return ({ name: "draggable" }); },
        onInjectProps: function (props) {
            var records = props.records;
            var columns = __spread(props.columns);
            if (hideHandler !== true) {
                columns = injectPropsIfTargetNotExisted(props.columns, targetColumnKey, {
                    key: fallbackColumnKey,
                    width: width,
                    header: null,
                    render: function () { return null; },
                });
            }
            total = records.length;
            return __assign(__assign({}, props), { columns: columns, draggable: true });
        },
        onInjectColumn: function (render) { return function (record, rowKey, recordIndex, column, columnIndex) {
            var result = render(record, rowKey, recordIndex, column, columnIndex);
            // 非表头
            if (columnIndex === -1) {
                return result;
            }
            if (recordIndex !== -1 &&
                (column.key === fallbackColumnKey || column.key === targetColumnKey)) {
                result.children = (React.createElement(DraggableTableRowIcon, { index: columnIndex, icon: icon, hideHandler: hideHandler }));
            }
            return {
                props: result.props,
                children: (React.createElement(DraggableTableCell, { index: columnIndex }, result.children)),
            };
        }; },
        onInjectRow: function (render) { return function (record, rowKey, recordIndex) {
            var args = [];
            for (var _i = 3; _i < arguments.length; _i++) {
                args[_i - 3] = arguments[_i];
            }
            var result = render.apply(void 0, __spread([record, rowKey, recordIndex], args));
            var row = (React.createElement(Draggable, { key: rowKey, draggableId: rowKey, index: recordIndex }, function (provided, snapshot) { return (React.createElement(DraggableTableRowContext.Provider, { value: { provided: provided } }, React.cloneElement(result.row, __assign(__assign(__assign({}, provided.draggableProps), (full ? provided.dragHandleProps : {})), { style: __assign(__assign(__assign({}, (result.row.props.style || {})), provided.draggableProps.style), (full ? provided.dragHandleProps.style : {})), ref: provided.innerRef, className: classNames(result.row.props.className, {
                    "is-dragging": snapshot.isDragging,
                }) })))); }));
            return __assign(__assign({}, result), { row: recordIndex === total - 1 ? (React.createElement(Fragment, { key: rowKey },
                    row,
                    React.createElement(DraggableTablePlaceHolder, null))) : (row) });
        }; },
        onInjectBody: function (render) { return function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return (React.createElement(DraggableTableBody, { module: module, onDragStart: onDragStart, onDragEnd: function (context) {
                    var _a, _b;
                    var records = args[0];
                    onDragEnd(reorder(records, (_a = context.source) === null || _a === void 0 ? void 0 : _a.index, (_b = context.destination) === null || _b === void 0 ? void 0 : _b.index), context);
                } }, render.apply(void 0, __spread(args))));
        }; },
    };
}
export var DraggableTableRowContext = React.createContext({});
var DraggableTableBody = React.forwardRef(function (_a, ref) {
    var module = _a.module, onDragEnd = _a.onDragEnd, onDragStart = _a.onDragStart, children = _a.children, props = __rest(_a, ["module", "onDragEnd", "onDragStart", "children"]);
    var DragDropContext = module.DragDropContext, Droppable = module.Droppable;
    var _b = __read(useState(false), 2), isDragging = _b[0], setIsDragging = _b[1];
    return (React.createElement(DragDropContext, { onBeforeDragStart: function () { return setIsDragging(true); }, onDragStart: function (context) { return onDragStart(context); }, onDragEnd: function (result) {
            setIsDragging(false);
            onDragEnd(result);
        } },
        React.createElement(Droppable, { droppableId: "droppable" }, function (provided) {
            return (React.createElement(TableDraggableContext.Provider, { value: {
                    isDragging: isDragging,
                    placeholder: provided.placeholder,
                } }, React.cloneElement(children, __assign(__assign({}, props), { ref: mergeRefs(provided.innerRef, ref), className: classNames(children.props.className, props.className, {
                    "is-dragging": isDragging,
                }) }))));
        })));
});
function DraggableTableRowIcon(_a) {
    var icon = _a.icon, index = _a.index, hideHandler = _a.hideHandler;
    var columnsWidths = useContext(TableContext).columnsWidths;
    var isDragging = useContext(TableDraggableContext).isDragging;
    var provided = useContext(DraggableTableRowContext).provided;
    if (!provided) {
        return null;
    }
    return (React.createElement("div", { style: isDragging
            ? { width: columnsWidths[index] - cellPaddingWidth }
            : undefined, className: classNames({
            "hover-icon": hideHandler === "unhover" && !isDragging,
        }) },
        React.createElement(Icon, __assign({ type: icon }, provided.dragHandleProps))));
}
function DraggableTablePlaceHolder() {
    var placeholder = useContext(TableDraggableContext).placeholder;
    return React.createElement(React.Fragment, null, placeholder);
}
function DraggableTableCell(_a) {
    var index = _a.index, children = _a.children;
    var columnsWidths = useContext(TableContext).columnsWidths;
    var isDragging = useContext(TableDraggableContext).isDragging;
    return (React.createElement("div", { style: {
            width: isDragging ? columnsWidths[index] - cellPaddingWidth : undefined,
        } }, children));
}
//# sourceMappingURL=draggable.js.map